import React, { useState, useCallback, FormEvent } from 'react';
import { isValidEmail } from '../utils/validation';
import './PasswordResetForm.css';

interface PasswordResetFormProps {
  onSubmit: (email: string) => Promise<void>;
  onBackToLogin: () => void;
  isLoading?: boolean;
}

interface FormErrors {
  email?: string;
}

/**
 * PasswordResetForm Component
 * Form for requesting a password reset link
 */
export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onSubmit,
  onBackToLogin,
  isLoading = false
}) => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Validate email field
  const validateField = useCallback((value: string): string | undefined => {
    if (!value) return 'Email is required';
    if (!isValidEmail(value)) return 'Please enter a valid email address';
    return undefined;
  }, []);

  // Handle field blur
  const handleBlur = useCallback(() => {
    setTouched({ email: true });
    const error = validateField(email);
    setErrors({ email: error });
  }, [email, validateField]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const error = validateField(email);
    setErrors({ email: error });
    setTouched({ email: true });
    return !error;
  }, [email, validateField]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(email);
      setIsSuccess(true);
      
      // Start resend countdown (60 seconds)
      setCanResend(false);
      setResendCountdown(60);
      
      const interval = setInterval(() => {
        setResendCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Password reset failed:', error);
    }
  }, [email, validateForm, onSubmit]);

  // Handle resend
  const handleResend = useCallback(async () => {
    if (!canResend) return;

    try {
      await onSubmit(email);
      
      // Restart countdown
      setCanResend(false);
      setResendCountdown(60);
      
      const interval = setInterval(() => {
        setResendCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Resend failed:', error);
    }
  }, [email, canResend, onSubmit]);

  if (isSuccess) {
    return (
      <div className="password-reset-form">
        <div className="password-reset-success">
          <div className="password-reset-success-icon">✅</div>
          <h2 className="password-reset-success-title">Check Your Email</h2>
          
          <p className="password-reset-success-message">
            We've sent a password reset link to
          </p>
          <p className="password-reset-success-email">{email}</p>
          
          <p className="password-reset-success-note">
            The link will expire in 24 hours.
          </p>

          <div className="password-reset-success-actions">
            <p className="password-reset-resend-text">
              Didn't receive the email?
            </p>
            <button
              type="button"
              className="password-reset-resend-button"
              onClick={handleResend}
              disabled={!canResend || isLoading}
            >
              {!canResend ? `Resend in ${resendCountdown}s` : 'Resend Link'}
            </button>
          </div>

          <button
            type="button"
            className="password-reset-back-button"
            onClick={onBackToLogin}
            disabled={isLoading}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-form">
      <button
        className="password-reset-back"
        onClick={onBackToLogin}
        type="button"
        disabled={isLoading}
      >
        ← Back to Login
      </button>

      <h2 className="password-reset-title">Reset Your Password</h2>

      <p className="password-reset-description">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {/* Email Field */}
        <div className="password-reset-field">
          <label htmlFor="reset-email" className="password-reset-label">
            Email Address
          </label>
          <input
            id="reset-email"
            type="email"
            className={`password-reset-input ${
              touched.email && errors.email ? 'password-reset-input-error' : ''
            }`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={handleBlur}
            placeholder="your.email@example.com"
            disabled={isLoading}
            required
            autoComplete="email"
            autoFocus
          />
          {touched.email && errors.email && (
            <span className="password-reset-error">{errors.email}</span>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="password-reset-submit"
          disabled={isLoading}
        >
          {isLoading ? '⟳ Sending...' : 'Send Reset Link'}
        </button>

        {/* Footer */}
        <div className="password-reset-footer">
          Remember your password?{' '}
          <button
            type="button"
            className="password-reset-link"
            onClick={onBackToLogin}
            disabled={isLoading}
          >
            Login
          </button>
        </div>
      </form>
    </div>
  );
};

export default PasswordResetForm;
